import type { Deliverable, Project } from '../types/portal'
import { deliverableStage } from './deliverables'

/**
 * The project's headline status, derived from its deliverables so Portal and
 * client can never see contradictory states.
 */
export function projectStatus(project: Project): string {
  const items = project.deliverables

  if (project.archived) return 'Complete'
  if (items.length === 0) return 'Not Started'
  if (items.every((item) => item.status === 'Delivered')) return 'Complete'

  if (items.some((item) => item.status === 'Ready for Review')) return 'Awaiting Client Review'

  /* Only genuinely outstanding feedback counts. Completed requests are history
     and must never keep a project flagged as needing changes. */
  if (items.some((item) => item.changeRequests.some((request) => !request.resolved))) {
    return 'Changes in Progress'
  }

  if (items.some((item) => item.status === 'Changes Requested')) return 'Changes in Progress'

  /* Stage follows status, so the project reads from the furthest work along. */
  if (items.some((item) => item.status === 'Editing')) return 'Post-Production'
  if (items.some((item) => item.status === 'In Production')) return 'In Production'

  if (items.some((item) => deliverableStage(item.status) === 'Final Delivery')) {
    return 'Final Delivery'
  }

  return 'Not Started'
}

export interface ClientHeadline {
  heading: 'Your Next Action' | 'Project Status'
  message: string
  support: string
  /** The deliverable the client should open, when there is one. */
  actionId?: string
  actionLabel?: string
}

const NOTHING_REQUIRED = 'Nothing is required from you right now.'

/**
 * Derives the client's headline from the real state of the project, in the
 * priority order Portal defined: client actions first, then project progress.
 */
export function clientHeadline(
  project: Project,
  visible: Deliverable[],
  complete: boolean,
): ClientHeadline {
  const awaiting = visible.filter((item) => item.status === 'Ready for Review')

  if (awaiting.length === 1) {
    const item = awaiting[0]
    return {
      heading: 'Your Next Action',
      message: `${item.name} is ready for your review.`,
      support: item.version
        ? `Please review ${item.version} and approve it or request changes.`
        : 'Please review the latest version and approve it or request changes.',
      actionId: item.id,
      actionLabel: item.version ? `Review ${item.version}` : `Review ${item.name}`,
    }
  }

  if (awaiting.length > 1) {
    return {
      heading: 'Your Next Action',
      message: `${awaiting.length} deliverables are ready for your review.`,
      support: 'Please review the latest versions and approve them or request changes.',
      actionId: awaiting[0].id,
      actionLabel: 'Start Reviewing',
    }
  }

  if (complete) {
    return {
      heading: 'Project Status',
      message: 'Your project is complete.',
      support: 'All final files are ready.',
    }
  }

  const changing = visible.find((item) => item.status === 'Changes Requested')
  if (changing) {
    return {
      heading: 'Project Status',
      message: `We’re making the changes requested for ${changing.name}.`,
      support: 'We’ll let you know when the next version is ready.',
    }
  }

  /* Each of these is simply what the status means: Editing is post-production,
     In Production is production, Not Started is pre-production. */
  const inEdit = visible.find((item) => item.status === 'Editing')
  const anyProduction = visible.some((item) => item.status === 'In Production')
  const anyPreProduction = visible.some((item) => item.status === 'Not Started')

  if (!inEdit && anyPreProduction && !anyProduction) {
    return {
      heading: 'Project Status',
      message: 'We’re currently in pre-production.',
      support: NOTHING_REQUIRED,
    }
  }

  if (!inEdit && anyProduction) {
    return {
      heading: 'Project Status',
      message: 'Production is underway.',
      support: NOTHING_REQUIRED,
    }
  }

  if (inEdit) {
    return {
      heading: 'Project Status',
      message: `${inEdit.name} is now in post-production.`,
      support: 'We’ll let you know when it’s ready for review.',
    }
  }

  /* Everything signed off, but the finals are not out yet. */
  const allApproved =
    visible.length > 0 &&
    visible.every((item) => deliverableStage(item.status) === 'Final Delivery')
  const finalsPending = visible.some((item) => item.status === 'Approved')

  if (allApproved && finalsPending) {
    return {
      heading: 'Project Status',
      message: 'Everything is approved. We’re preparing your final files.',
      support: NOTHING_REQUIRED,
    }
  }

  const approved = [...visible].reverse().find((item) => item.status === 'Approved')
  if (approved) {
    return {
      heading: 'Project Status',
      message: `${approved.name} is approved.`,
      support: NOTHING_REQUIRED,
    }
  }

  if (visible.length === 0) {
    return {
      heading: 'Project Status',
      message: 'We’re setting your project up.',
      support: NOTHING_REQUIRED,
    }
  }

  return {
    heading: 'Project Status',
    message: `${project.name} is underway.`,
    support: NOTHING_REQUIRED,
  }
}
